// src/identity.ts
var accountStatuses = ["pending", "active", "disabled"];
var membershipStatuses = ["active", "disabled", "removed"];
var sessionTypes = ["office", "registered_device"];
var deviceKinds = ["browser", "phone", "tablet", "desktop", "unknown"];
var mfaVerificationMethods = ["totp", "recovery_code"];

// src/permissions.ts
var permissionIds = [
  "business.manage_settings",
  "business.manage_branding",
  "business.manage_email",
  "users.manage",
  "permissions.delegate",
  "devices.manage",
  "templates.build",
  "templates.publish",
  "templates.retire",
  "templates.folders_manage",
  "templates.examples_submit",
  "reference.manage",
  "jobs.create",
  "jobs.self_dispatch",
  "jobs.assign",
  "jobs.dispatch",
  "jobs.recall_cancel",
  "jobs.view_active",
  "reports.review",
  "reports.download",
  "reports.resend",
  "reports.void",
  "reports.archive",
  "exports.create",
  "audit.view",
  "legal_hold.manage"
];
var permissionSet = new Set(permissionIds);
function isPermissionId(value) {
  return permissionSet.has(value);
}

// src/reference-data.ts
var referenceListStatuses = ["active", "archived"];
var referenceListVersionStatuses = ["draft", "published"];
var referenceListSourceTypes = ["manual", "csv", "xlsx", "copy"];
var referenceImportSourceTypes = ["csv", "xlsx"];
var referenceImportStatuses = [
  "queued",
  "processing",
  "mapping_required",
  "preview_ready",
  "applied",
  "failed",
  "discarded"
];
var referenceImportClassifications = [
  "new",
  "changed",
  "unchanged",
  "duplicate",
  "invalid"
];

// src/templates.ts
var templateStatuses = ["draft", "published", "retired", "archived"];
var templateVersionStatuses = ["draft", "published"];
var templateFolderStatuses = ["active", "archived"];
var templateAvailabilityScopeTypes = ["business", "membership", "team"];
var fieldOwnerships = ["coordinator", "engineer", "system", "readonly"];
var fieldCategories = [
  "text",
  "number",
  "contact",
  "address",
  "date_time",
  "choice",
  "media",
  "evidence",
  "location",
  "layout",
  "structure",
  "logic",
  "system"
];
var formFieldTypes = [
  "single_line_text",
  "multi_line_text",
  "integer",
  "decimal",
  "currency",
  "email",
  "telephone",
  "address",
  "uk_postcode",
  "date",
  "time",
  "date_time",
  "dropdown",
  "radio_buttons",
  "checkboxes",
  "toggle",
  "file_upload",
  "single_photograph",
  "multiple_photographs",
  "signature",
  "gps",
  "map_location",
  "what3words",
  "heading",
  "instruction_text",
  "divider",
  "repeating_section",
  "table_grid",
  "calculated_field",
  "system_field"
];
var allOwnerships = fieldOwnerships;
var officeAndEngineerOwnerships = [
  "coordinator",
  "engineer"
];
var contextOwnerships = [
  "coordinator",
  "engineer",
  "readonly"
];
var layoutOwnerships = ["readonly"];
var systemOwnerships = ["system", "readonly"];
var fieldTypeRegistry = [
  {
    id: "single_line_text",
    category: "text",
    label: "Single-line text",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "multi_line_text",
    category: "text",
    label: "Multi-line text",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "integer",
    category: "number",
    label: "Integer",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "decimal",
    category: "number",
    label: "Decimal",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "currency",
    category: "number",
    label: "Currency",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "email",
    category: "contact",
    label: "Email",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "telephone",
    category: "contact",
    label: "Telephone",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "address",
    category: "address",
    label: "Address",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 4
  },
  {
    id: "uk_postcode",
    category: "address",
    label: "UK postcode",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "date",
    category: "date_time",
    label: "Date",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "time",
    category: "date_time",
    label: "Time",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "date_time",
    category: "date_time",
    label: "Date and time",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "dropdown",
    category: "choice",
    label: "Dropdown",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "radio_buttons",
    category: "choice",
    label: "Radio buttons",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "checkboxes",
    category: "choice",
    label: "Checkboxes",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "toggle",
    category: "choice",
    label: "Toggle",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "file_upload",
    category: "media",
    label: "File upload",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 8
  },
  {
    id: "single_photograph",
    category: "media",
    label: "Single photograph",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 8
  },
  {
    id: "multiple_photographs",
    category: "media",
    label: "Multiple photographs",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 8
  },
  {
    id: "signature",
    category: "evidence",
    label: "Signature",
    allowedOwnerships: ["engineer"],
    valueBearing: true,
    phaseIntroduced: 8
  },
  {
    id: "gps",
    category: "location",
    label: "Current GPS",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 8
  },
  {
    id: "map_location",
    category: "location",
    label: "Map location",
    allowedOwnerships: officeAndEngineerOwnerships,
    valueBearing: true,
    phaseIntroduced: 8
  },
  {
    id: "what3words",
    category: "location",
    label: "What3words",
    allowedOwnerships: contextOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "heading",
    category: "layout",
    label: "Heading",
    allowedOwnerships: layoutOwnerships,
    valueBearing: false,
    phaseIntroduced: 2
  },
  {
    id: "instruction_text",
    category: "layout",
    label: "Instruction text",
    allowedOwnerships: layoutOwnerships,
    valueBearing: false,
    phaseIntroduced: 2
  },
  {
    id: "divider",
    category: "layout",
    label: "Divider",
    allowedOwnerships: layoutOwnerships,
    valueBearing: false,
    phaseIntroduced: 2
  },
  {
    id: "repeating_section",
    category: "structure",
    label: "Repeating section",
    allowedOwnerships: allOwnerships,
    valueBearing: false,
    phaseIntroduced: 4
  },
  {
    id: "table_grid",
    category: "structure",
    label: "Table/grid",
    allowedOwnerships: allOwnerships,
    valueBearing: false,
    phaseIntroduced: 4
  },
  {
    id: "calculated_field",
    category: "logic",
    label: "Calculated field",
    allowedOwnerships: ["system"],
    valueBearing: true,
    phaseIntroduced: 2
  },
  {
    id: "system_field",
    category: "system",
    label: "Auto-populated field",
    allowedOwnerships: systemOwnerships,
    valueBearing: true,
    phaseIntroduced: 2
  }
];
var fieldTypeMap = new Map(
  fieldTypeRegistry.map((fieldType) => [fieldType.id, fieldType])
);
function getFieldTypeDefinition(fieldType) {
  const definition = fieldTypeMap.get(fieldType);
  if (!definition) throw new Error(`Unknown field type ${fieldType}.`);
  return definition;
}
function isFormFieldType(value) {
  return fieldTypeMap.has(value);
}
function fieldTypeAllowsOwnership(fieldType, ownership) {
  return getFieldTypeDefinition(fieldType).allowedOwnerships.includes(ownership);
}

// src/index.ts
var platformName = "Dispatch Platform v2";
var architecture = "modular-monolith";
var releasePolicy = "complete-product-only";
export {
  accountStatuses,
  architecture,
  deviceKinds,
  fieldCategories,
  fieldOwnerships,
  fieldTypeAllowsOwnership,
  fieldTypeRegistry,
  formFieldTypes,
  getFieldTypeDefinition,
  isFormFieldType,
  isPermissionId,
  membershipStatuses,
  mfaVerificationMethods,
  permissionIds,
  platformName,
  referenceImportClassifications,
  referenceImportSourceTypes,
  referenceImportStatuses,
  referenceListSourceTypes,
  referenceListStatuses,
  referenceListVersionStatuses,
  releasePolicy,
  sessionTypes,
  templateAvailabilityScopeTypes,
  templateFolderStatuses,
  templateStatuses,
  templateVersionStatuses
};
